// src/pages/Admin/AdminLayout.tsx
import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../hooks/useAuth';
import {
  LayoutDashboard, Users, Bike, ShoppingBag, CreditCard, Headphones, Settings,
  LogOut, Menu, X, ChevronRight, Zap, TrendingUp, ShieldCheck, User
} from 'lucide-react';
import NotificationBell from '../../components/shared/NotificationBell';

const AdminLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { to: '/admin', icon: <LayoutDashboard className="h-5 w-5" />, label: 'Dashboard', end: true },
    { to: '/admin/users', icon: <Users className="h-5 w-5" />, label: 'Users' },
    { to: '/admin/riders', icon: <Bike className="h-5 w-5" />, label: 'Riders' },
    { to: '/admin/orders', icon: <ShoppingBag className="h-5 w-5" />, label: 'Orders' },
    { to: '/admin/payments', icon: <CreditCard className="h-5 w-5" />, label: 'Payments' },
    { to: '/admin/tickets', icon: <Headphones className="h-5 w-5" />, label: 'Support Tickets' },
    { to: '/admin/settings', icon: <Settings className="h-5 w-5" />, label: 'Settings' },
    { to: '/admin/profile', icon: <User className="h-5 w-5" />, label: 'Profile' },
  ];

  const quickActions = [
    {
      icon: <Users className="h-5 w-5" />,
      title: 'Manage Users',
      description: 'View and manage all users',
      action: () => navigate('/admin/users'),
      color: 'bg-blue-50 border-blue-100'
    },
    {
      icon: <Bike className="h-5 w-5" />,
      title: 'Rider Verification',
      description: 'Verify new rider accounts',
      action: () => navigate('/admin/riders?filter=pending'),
      color: 'bg-green-50 border-green-100'
    },
    {
      icon: <Headphones className="h-5 w-5" />,
      title: 'Open Tickets',
      description: 'Respond to support tickets',
      action: () => navigate('/admin/tickets?status=open'),
      color: 'bg-orange-50 border-orange-100'
    },
    {
      icon: <Settings className="h-5 w-5" />,
      title: 'Platform Settings',
      description: 'Adjust system settings',
      action: () => navigate('/admin/settings'),
      color: 'bg-purple-50 border-purple-100'
    },
  ];

  const performanceMetrics = [
    { label: 'Total Revenue', value: '₦0', color: 'text-emerald-600' },
    { label: 'Active Users', value: '0', color: 'text-blue-600' },
    { label: 'Open Tickets', value: '0', color: 'text-orange-600' },
    { label: 'Completed Orders', value: '0', color: 'text-green-600' },
  ];

  if (!user || (user.role !== 'admin' && user.role !== 'support')) {
    return <div className="p-8 text-center">Access Denied</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-emerald-50">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-20 w-72 h-72 bg-gradient-to-r from-emerald-200/10 to-green-300/10 rounded-full mix-blend-multiply filter blur-3xl opacity-5 animate-blob"></div>
        <div className="absolute top-1/3 -right-20 w-72 h-72 bg-gradient-to-r from-blue-200/10 to-cyan-300/10 rounded-full mix-blend-multiply filter blur-3xl opacity-5 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-32 left-1/3 w-72 h-72 bg-gradient-to-r from-purple-200/10 to-pink-300/10 rounded-full mix-blend-multiply filter blur-3xl opacity-5 animate-blob animation-delay-4000"></div>
      </div>

      {/* Desktop Sidebar */}
      <motion.aside
        initial={{ x: -100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="w-80 bg-white border-r border-gray-200 hidden lg:block lg:fixed lg:inset-y-0 lg:left-0 lg:z-40"
      >
        <div className="relative h-full flex flex-col">
          {/* Header */}
          <div className="p-4 border-b border-gray-200 bg-white">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center">
                <ShieldCheck className="h-4 w-4 text-white" />
              </div>
              <span className="font-bold text-gray-900">Admin Panel</span>
            </div>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto pb-24">
            <div className="p-4">
              {/* User Profile */}
              <div className="flex items-center gap-3 mb-6 p-3 bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center text-white font-bold">
                  {user?.fullName?.charAt(0) || 'A'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{user?.fullName || 'Admin'}</p>
                  <p className="text-xs text-gray-500 truncate">{user?.email || 'admin@carryngo.com'}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full capitalize">
                      {user?.role}
                    </span>
                  </div>
                </div>
              </div>

              {/* Navigation */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <LayoutDashboard className="h-4 w-4 text-emerald-600" />
                  <h3 className="font-semibold text-gray-900">Navigation</h3>
                </div>
                <div className="space-y-2">
                  {navItems.map((item, index) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) =>
                        `w-full flex items-center gap-3 px-3 py-3 rounded-xl border transition-all duration-300 text-left group ${
                          isActive
                            ? 'bg-emerald-600 text-white border-emerald-600 shadow-md'
                            : 'bg-white border-gray-200 text-gray-700 hover:border-emerald-400 hover:bg-emerald-50'
                        }`
                      }
                    >
                      <div className="h-8 w-8 rounded-lg flex items-center justify-center">
                        {item.icon}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">{item.label}</p>
                      </div>
                      <ChevronRight className="h-3 w-3 text-gray-400 group-hover:text-gray-600 group-hover:translate-x-1 transition-all" />
                    </NavLink>
                  ))}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="h-4 w-4 text-emerald-600" />
                  <h3 className="font-semibold text-gray-900">Quick Actions</h3>
                </div>
                <div className="space-y-2">
                  {quickActions.map((action, index) => (
                    <motion.button
                      key={action.title}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      onClick={action.action}
                      className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl border ${action.color} hover:shadow-md transition-all duration-300 text-left group`}
                    >
                      <div className="h-8 w-8 rounded-lg flex items-center justify-center">
                        {action.icon}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 text-sm">{action.title}</p>
                        <p className="text-xs text-gray-600">{action.description}</p>
                      </div>
                      <ChevronRight className="h-3 w-3 text-gray-400 group-hover:text-gray-600 group-hover:translate-x-1 transition-all" />
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* Performance Metrics */}
              <div className="p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-200">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="h-4 w-4 text-gray-700" />
                  <span className="text-sm font-medium text-gray-900">Platform Overview</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {performanceMetrics.map((metric) => (
                    <div key={metric.label} className="text-center p-2 bg-white rounded-lg border border-gray-100">
                      <div className={`text-lg font-bold ${metric.color} mb-1`}>{metric.value}</div>
                      <div className="text-xs text-gray-600">{metric.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Logout Button */}
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200 bg-white">
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl text-red-600 hover:bg-red-50 transition-colors border border-red-200"
            >
              <LogOut className="h-5 w-5" />
              <span className="text-sm font-medium">Logout</span>
            </button>
          </div>
        </div>
      </motion.aside>

      {/* Desktop Header */}
      <div className="hidden lg:block fixed top-0 right-0 left-80 z-30 bg-white border-b border-gray-200 px-4 py-3 shadow-sm">
        <div className="flex items-center justify-end gap-4">
          <NotificationBell />
          <div className="flex items-center gap-2 ml-2">
            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center text-white font-bold text-sm">
              {user?.fullName?.charAt(0) || 'A'}
            </div>
            <span className="text-sm font-medium text-gray-700">{user?.fullName?.split(' ')[0] || 'Admin'}</span>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 lg:hidden"
              onClick={() => setMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ x: -320 }}
              animate={{ x: 0 }}
              exit={{ x: -320 }}
              className="fixed left-0 top-0 h-full w-80 bg-white shadow-2xl z-50 lg:hidden overflow-y-auto"
            >
              <div className="p-4 border-b border-gray-200 bg-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center">
                      <ShieldCheck className="h-4 w-4 text-white" />
                    </div>
                    <span className="font-bold text-gray-900">Admin Panel</span>
                  </div>
                  <button onClick={() => setMobileMenuOpen(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                    <X className="h-5 w-5 text-gray-600" />
                  </button>
                </div>
              </div>
              <div className="p-4 pb-20">
                <div className="flex items-center gap-3 mb-6 p-3 bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl">
                  <div className="h-12 w-12 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center text-white font-bold text-lg">
                    {user?.fullName?.charAt(0) || 'A'}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{user?.fullName || 'Admin'}</p>
                    <p className="text-xs text-gray-500">{user?.email || 'admin@carryngo.com'}</p>
                  </div>
                </div>
                <div className="space-y-2">
                  {navItems.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      onClick={() => setMobileMenuOpen(false)}
                      className={({ isActive }) =>
                        `w-full flex items-center gap-3 px-3 py-3 rounded-xl border ${
                          isActive
                            ? 'bg-emerald-600 text-white border-emerald-600'
                            : 'bg-white border-gray-200 text-gray-700 hover:border-emerald-400'
                        }`
                      }
                    >
                      <div className="h-8 w-8 rounded-lg flex items-center justify-center">
                        {item.icon}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">{item.label}</p>
                      </div>
                    </NavLink>
                  ))}
                  {quickActions.map((action) => (
                    <button
                      key={action.title}
                      onClick={() => {
                        action.action();
                        setMobileMenuOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl border ${action.color} text-left`}
                    >
                      <div className="h-8 w-8 rounded-lg flex items-center justify-center">
                        {action.icon}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 text-sm">{action.title}</p>
                        <p className="text-xs text-gray-600">{action.description}</p>
                      </div>
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                  className="w-full mt-6 flex items-center justify-center gap-3 px-4 py-3 rounded-xl text-red-600 hover:bg-red-50 border border-red-200"
                >
                  <LogOut className="h-5 w-5" />
                  <span className="text-sm font-medium">Logout</span>
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="lg:ml-80 min-h-screen pt-20 lg:pt-20">
        {/* Mobile Header */}
        <div className="lg:hidden sticky top-0 z-40 bg-white border-b border-gray-200 px-4 py-3 shadow-sm">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <Menu className="h-6 w-6 text-gray-700" />
            </button>
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center">
                <ShieldCheck className="h-4 w-4 text-white" />
              </div>
              <span className="font-bold text-gray-900">Admin</span>
            </div>
            <NotificationBell />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-6 lg:py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;